import React from 'react';
import { Snackbar, Box, Typography, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

interface RebookSnackbarProps {
  open: boolean;
  onDismiss: () => void;
  onBookAgain: () => void;
  message?: string;
}

const RebookSnackbar: React.FC<RebookSnackbarProps> = ({ open, onDismiss, onBookAgain, message = 'Same as last time?' }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  return (
    <Snackbar
      open={open}
      onClose={(_, reason) => {
        if (reason === 'clickaway') return;
        onDismiss();
      }}
      anchorOrigin={{
        vertical: isDesktop ? 'top' : 'bottom',
        horizontal: isDesktop ? 'right' : 'center',
      }}
      sx={{
        position: 'absolute',
        zIndex: 3,
        px: { xs: 2, sm: 0 },
        minWidth: 380,
        maxWidth: 500,
      }}
      ContentProps={{
        sx: {
          px: 2,
          py: 1.4,
          borderRadius: '14px',
          backgroundColor: alpha(theme.palette.primary.main, 0.85),
          border: `1px solid ${alpha(theme.palette.primary.light, 0.6)}`,
          boxShadow: `0 18px 36px ${alpha(theme.palette.primary.dark, 0.55)}`,
          backdropFilter: 'blur(12px)',
          color: theme.palette.primary.contrastText,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          '& .MuiSnackbarContent-message': {
            padding: 0,
          },
          '& .MuiSnackbarContent-action': {
            marginRight: 0,
            paddingLeft: theme.spacing(1),
          },
        },
      }}
      message={
        <Box sx={{ textAlign: 'left' }}>
          <Typography
            variant="subtitle1"
            sx={{
              whiteSpace: 'nowrap',
              fontSize: { xs: '0.9rem', sm: '1rem' },
            }}
          >
            {message}
          </Typography>
        </Box>
      }
      action={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1 }, flexWrap: 'nowrap' }}>
          <Button
            aria-label="Book again"
            variant="outlined"
            color="inherit"
            onClick={onBookAgain}
            sx={{
              px: { xs: 1.6, sm: 2.2 },
              py: { xs: 0.6, sm: 0.9 },
              fontSize: { xs: '0.85rem', sm: '0.95rem' },
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              boxShadow: 'none',
              borderColor: alpha(theme.palette.primary.contrastText, 0.7),
              color: theme.palette.primary.contrastText,
              '&:hover': {
                borderColor: theme.palette.primary.contrastText,
                backgroundColor: alpha(theme.palette.primary.contrastText, 0.12),
              },
            }}
          >
            Book Again
          </Button>
          <IconButton
            aria-label="Dismiss rebook prompt"
            onClick={onDismiss}
            size="small"
            sx={{
              color: theme.palette.primary.contrastText,
              borderRadius: '999px',
              border: `1px solid ${alpha(theme.palette.primary.contrastText, 0.3)}`,
              mx: 1,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      }
    />
  );
};

export default RebookSnackbar;
